// ADR-0018 §6.3 — WHICH TENANT a multi-tenant person is working in, as explicit state.
//
// A person who belongs to one tenant never reaches any of this: their token carries no membership, the
// picker is never drawn, nothing is stored, and every function here answers "no selection" for them
// forever. For a person whose token DOES carry memberships (§3.1, `/tenants/<uuid>/<ROLE>`), the choice is
// what decides which organisation their next write lands in — so it is stored, not derived, and it travels
// on every request as `X-Tenant` (src/lib/api.ts `tenantHint`).
//
// WHY STORED STATE AND NOT A URL. Production addresses tenants by path on one host
// (`https://versopeople.com/t/<slug>/…`, docs/infra/production-deploy.md), and the four `/t/:slug/` routes
// the SPA has are PRE-SESSION only — the authenticated shell is apex-only (src/app/router.tsx). So after
// login there is no slug in any URL to navigate between, and changing the choice changes the header the
// next request carries: that is the whole switch. The token is untouched, no authorization request is made,
// and `kc_idp_hint` never enters the picture.
//
// The fourth of those routes (task f71bf1eb) is the one that looks like an exception and is not:
// `/t/:slug/questionnaires/:id/respond` is where the mailed questionnaire link lands, and it draws no page
// of the app — it forwards to the apex form on a session, and without one it hands the arrival straight to
// that tenant's identity provider (task 09e495b1, src/app/TenantRespondEntry.tsx). The slug names the
// organisation for the sign-in hop and is then dropped, so nothing inside the shell has ever had two
// spellings.
//
// THE URL STILL WINS WHERE IT NAMES A TENANT. A per-tenant subdomain or a `/t/<slug>/` address is an
// explicit statement about which tenant the page is for, and a bookmarked one has to keep working — so
// `resolveTenantSlug()` is read first in both places this is read, and the choice answers for the apex
// host, where the authenticated app has no slug anywhere.
//
// WHY "NOTHING CHOSEN" AND "MY OWN TENANT" ARE DIFFERENT VALUES. Both send no `X-Tenant` and both land in
// the tenant the token names, so one value could carry them — and then a person who had deliberately chosen
// their own organisation would meet the picker again on every load, with the app unable to tell their answer
// from silence. §6.1's decision is that the tenant is CHOSEN after login; a choice the app forgets is not
// one.
//
// ONE ACTIVE VALUE PER PAGE, WHICH IS WHAT THE PAGE SHOWS AND WHAT EVERY REQUEST CARRIES. `localStorage` is
// per ORIGIN and every tenant shares one, so the stored value is not per page: another tab can change it
// under this one at any moment. Read per request, it would route this page's next write to a tenant this
// page never named — the shell would still say organisation A while the header said B, which is the exact
// hazard §6 exists to remove. So storage is read ONCE, into `active` below, and both readers answer from
// that: `selectedTenantSlug()` for the header and the provider's state for the pill (auth/tenantPicker.tsx).
// A change made ELSEWHERE therefore cannot move this page's routing at all — it is reconciled by replacing
// the page (`externalChoiceChanged`), which is the only honest way to adopt it: every row on screen was read
// in the old tenant.
//
// NO `src/config` IMPORT, deliberately: that module reads `import.meta.env` and cannot be imported outside
// Vite, which would make this file untestable (the reason src/auth/idp-hint-wiring.test.mjs is a source
// scan rather than a behavioural test). The URL slug is therefore a PARAMETER here, resolved by the callers
// — which are the two modules that already import config anyway.

import type { components } from "@clms/contracts";

/**
 * One tenant the caller holds a membership in, as `GET /api/auth/memberships` answers it (ADR-0018 §6.2,
 * carried in `tenant_memberships` since ADR-0021 §4.1). The DTO is the generated one — never hand-written —
 * so the picker cannot drift from what the backend discloses.
 *
 * SINCE ADR-0021 §5 IT CARRIES `roles`, and that is not decoration: the SPA used to derive a membership
 * session's roles from the access token's `groups` claim, §4.1 took that claim away, and §9's answer is
 * that the app is TOLD them on the call it already makes. So the selection below is what `capabilities.ts`
 * reads a membership session's authority from, which is why the roles are stored with the choice rather
 * than looked up again per render: the stored value has to answer on the first paint of a reloaded
 * document, before the memberships read of that load has landed.
 */
export type TenantMembership = components["schemas"]["TenantMembershipResponse"];

/**
 * What the person chose: their OWN tenant (the `tenant_id` their token carries, which the backend resolves
 * when no hint is sent), or one of their memberships. Absent — `storedChoice()` answering null — means they
 * have not chosen yet, which is what the picker is drawn for.
 */
export type TenantChoice =
  | { kind: "own" }
  | { kind: "membership"; membership: TenantMembership };

/**
 * Where the choice lives. `localStorage`, beside the session and the tokens, because it has to survive a
 * reload and a step-up round trip — a choice lost on the way back from Keycloak would land the person's
 * retried write in their own tenant instead of the customer's. Per ORIGIN, like the tokens: if per-tenant
 * subdomains are ever deployed, the other origin starts with no choice and no token, which is §6.3's
 * cross-origin case and is deliberately not implemented.
 */
const CHOICE_KEY = "clms.tenantChoice";

/**
 * A stored or broadcast value, as a choice. Tolerant by construction: a corrupt or half-written value reads
 * as "not chosen", so the worst a bad write can do is ask the question again — never send a header naming a
 * tenant nobody picked.
 */
function parseChoice(raw: string | null): TenantChoice | null {
  try {
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { kind?: unknown; membership?: Partial<TenantMembership> };
    if (parsed?.kind === "own") return { kind: "own" };
    if (parsed?.kind !== "membership") return null;

    const membership = parsed.membership;
    // `roles` is REQUIRED and non-empty, like the slug: a membership carrying no role is not a tenant
    // anybody reaches (the backend leaves it out of the answer), and a value stored before ADR-0021 §5
    // added the field carries none — reading that as "a membership with no authority" would quietly put a
    // consultant inside the customer with a learner's app. Unparseable is "not chosen", so the picker asks
    // again and the fresh answer brings the roles with it.
    const roles = (membership?.roles as unknown[] | undefined ?? [])
      .filter((role): role is string => typeof role === "string" && role.length > 0);
    return typeof membership?.slug === "string"
      && membership.slug.length > 0
      && typeof membership.tenantId === "string"
      && typeof membership.name === "string"
      && roles.length > 0
      ? {
          kind: "membership",
          membership: {
            tenantId: membership.tenantId,
            slug: membership.slug,
            name: membership.name,
            roles,
          },
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * Whether two role sets are the same authority. By CONTENT and not by position: the backend answers them
 * in ordinal order today, but nothing may acquire a dependency on an order (§1.1 M-2 is the same rule for
 * the claim), and "the same roles in a different order" must not re-store the choice on every load.
 */
function sameRoles(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = (roles: readonly string[]) => [...roles].sort().join("\u0000");
  return sorted(a) === sorted(b);
}

/** What storage says right now, parsed. The page's own answer is {@link activeChoice}, not this. */
export function storedChoice(): TenantChoice | null {
  try {
    return parseChoice(localStorage.getItem(CHOICE_KEY));
  } catch {
    return null; // a privacy mode that refuses even reads
  }
}

/**
 * THE tenant of this page: the one value its requests are routed by and its shell is labelled from. Read
 * out of storage once (see the header) and moved only by this page — never by another tab.
 */
let active: TenantChoice | null = null;
let activated = false;

/** The page's active choice, establishing it from storage the first time anything asks. */
export function activeChoice(): TenantChoice | null {
  if (!activated) {
    active = storedChoice();
    activated = true;
  }
  return active;
}

/**
 * Establish the stored choice as this page's active one again. Called when a SESSION appears
 * (auth/tenantPicker.tsx): a page that was signed out cleared the choice, and a session seeded into that
 * same page — the E2E harness and the email-link redeem both do exactly this — arrives with whatever
 * storage holds for it, which is the value its requests must then carry.
 */
export function activateChoice(): TenantChoice | null {
  activated = false;
  return activeChoice();
}

/**
 * Make `choice` this page's active tenant — what it shows AND what it routes to, inseparably — and store it
 * for the next load. Returns whether the STORE succeeded.
 *
 * The two happen in this order and the active value moves either way, because a refused write (a quota, a
 * privacy mode) must not be able to split the two apart: a page displaying the organisation it is also
 * sending every request to is correct even when nothing could be written down, and the only cost is the
 * question being asked again on the next load. What is NOT allowed is the opposite pair — a label that
 * moved while the header did not, or a header that moved while the page kept the old tenant's rows on
 * screen. The caller that reloads to adopt a choice uses {@link persistChoice} instead, precisely because a
 * value that cannot survive the reload must not be acted on at all.
 */
export function chooseTenant(choice: TenantChoice | null): boolean {
  active = choice;
  activated = true;
  return persistChoice(choice);
}

/**
 * Store the choice for the NEXT page load without touching this one's active tenant. The switch in the
 * topbar is the caller: it adopts the new tenant by reloading (every row on screen was read in the old one),
 * so the choice has to outlive this document — and if it cannot be stored, nothing may move, because a
 * switch this page applied to its header alone would leave the organisation it displays and the
 * organisation it writes to disagreeing.
 */
export function persistChoice(choice: TenantChoice | null): boolean {
  try {
    if (choice) {
      localStorage.setItem(CHOICE_KEY, JSON.stringify(choice));
    } else {
      localStorage.removeItem(CHOICE_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * WHICH TENANT a choice routes to, as one comparable value: the own tenant (no header), a named membership,
 * or nothing chosen. Two choices with the same key are the same organisation for every purpose this app has
 * — which is what makes it the right thing to compare an incoming change against, and the right thing for a
 * tenant-scoped effect to key on.
 */
export function choiceKey(choice: TenantChoice | null): string {
  if (choice === null) return "none";
  return choice.kind === "own" ? "own" : `membership:${choice.membership.slug}`;
}

/**
 * How the memberships read (`GET /api/auth/memberships`) stands: not asked, in flight, answered, or FAILED.
 * The provider holds it as state (auth/tenantPicker.tsx); the type lives here because
 * {@link settledTenantKey} is the decision it exists for, and that decision has to be readable — and
 * testable — without React.
 */
export type TenantReadStatus = "none" | "loading" | "ready" | "error";

/**
 * WHICH TENANT a tenant-scoped WRITE made on a signed-in load belongs in, as a key — and `null` for every
 * state in which that is still an open question, which is the answer that defers the write instead of
 * guessing at it (ADR-0018 §6.2). `useActiveTenantKey` is the hook over this.
 *
 * The four states, and why each answers as it does:
 * <ul>
 *   <li>NOT ASKED or IN FLIGHT — nothing is known about this person's organisations yet. Open.</li>
 *   <li>THE READ FAILED — an outage, an aborted load, a 401 the session hook is already handling. An
 *   established choice still answers: it is the tenant this page's requests are already carrying, so a
 *   write lands where the person last said. With NO choice there is no answer at all, and the request
 *   would carry no `X-Tenant` — leaving the backend to resolve the tenant the TOKEN names, which for a
 *   consultant is their own organisation rather than the customer they were about to be asked about. So
 *   it stays open, and the write waits for the next load, which reads the memberships again. Nothing is
 *   retried here on purpose: the cost of the failure is one deferred reconciliation, and a page that
 *   polled a failing read would pay for it on every load.</li>
 *   <li>ANSWERED, WITH MEMBERSHIPS AND NO CHOICE — the picker is on screen. Open by definition: this is
 *   the state the whole feature exists to put a question in front of.</li>
 *   <li>ANSWERED — settled. An EMPTY answer settles it too, and that is not a loophole: a person with no
 *   memberships has nothing to choose between, so their requests resolve to their own tenant with or
 *   without an answer, which is §6.1's promise that nothing changes for them.</li>
 * </ul>
 *
 * Keyed rather than boolean so the write can be made once PER TENANT — a person who switches organisation
 * is a person whose tenant-scoped row is a different row.
 */
export function settledTenantKey(
  status: TenantReadStatus,
  memberships: readonly TenantMembership[],
  choice: TenantChoice | null,
): string | null {
  if (status === "none" || status === "loading") return null;
  if (status === "error") return choice === null ? null : choiceKey(choice);
  if (memberships.length > 0 && choice === null) return null;
  return choiceKey(choice);
}

/**
 * Whether a `storage` event means ANOTHER TAB has moved the choice away from this page's active tenant —
 * the one case this page cannot answer by itself (auth/tenantPicker.tsx reloads on it).
 *
 * `key === null` is a whole-storage clear, which takes the choice with it. An event carrying the value this
 * page is already on is not a change: a prune that rewrote an identical value, or a tab that switched back,
 * leaves nothing to reconcile.
 */
export function externalChoiceChanged(event: { key: string | null; newValue: string | null }): boolean {
  if (event.key !== null && event.key !== CHOICE_KEY) return false;
  const incoming = event.key === null ? null : parseChoice(event.newValue);
  return choiceKey(incoming) !== choiceKey(activeChoice());
}

/** The chosen MEMBERSHIP, or null for the own tenant and for no choice at all. */
export function selectedTenant(): TenantMembership | null {
  const choice = activeChoice();
  return choice?.kind === "membership" ? choice.membership : null;
}

/**
 * The chosen membership's slug — the `X-Tenant` value, and null whenever the person is in their own tenant,
 * where the backend resolves the tenant the token names exactly as it did before any of this existed.
 */
export function selectedTenantSlug(): string | null {
  return selectedTenant()?.slug ?? null;
}

/**
 * Whether the tenant this session is working in was reached BY MEMBERSHIP — the one question step-up has to
 * ask (ADR-0018 §6.3).
 *
 * The rule is about the account and not about the tenant: a federated customer's own people must keep
 * getting `kc_idp_hint` on a re-authorization, because their credential is at their employer's directory and
 * a realm credential page would ask them for a password they have never had. An Incedo consultant inside
 * that same federated tenant is the mirror image — they are realm-local, and the hint would push them into
 * the customer's broker, where `CLMS first broker login` refuses them. True here is exactly that second
 * case: a membership decided the tenant, so the person is in it as a member rather than as one of the
 * customer's own.
 *
 * `urlSlug` is `resolveTenantSlug()`'s answer, passed in by the caller. A URL naming a DIFFERENT tenant from
 * the choice is the URL's tenant, not the membership's, so it is not this case.
 */
export function reachedByMembership(urlSlug: string | null): boolean {
  const membership = selectedTenant();
  if (!membership) return false;
  return urlSlug === null || urlSlug === membership.slug;
}

/**
 * Keep the stored choice honest against what the token now says, and answer the choice that still holds.
 *
 * Three things it settles, all of them states a person really reaches:
 * <ul>
 *   <li>a membership that was REVOKED (or a tenant that was deleted) is no longer in the answer, and a
 *   choice naming one would send `X-Tenant` for a tenant the token cannot enter — a 403 on every request,
 *   with no way out of it from inside the app. Cleared, so the picker asks again;</li>
 *   <li>a customer that RENAMED itself did so in its own settings, and a shell still showing the old label
 *   would be telling the person they are somewhere they are not. Re-read from the answer;</li>
 *   <li>a ROLE that was granted or revoked without the membership itself ending — the ordinary case on the
 *   operator's grant surface, which takes one code off a person who holds two. The stored roles are what
 *   `capabilities.ts` draws the nav from (ADR-0021 §9), so a stale set is a door that answers 403 or a door
 *   that is not offered at all. Re-read from the answer, like the name;</li>
 *   <li>a person with NO memberships has nothing to choose between, so any stored choice is a leftover —
 *   from a revoked grant, or from somebody else's session on a shared browser — and is cleared.</li>
 * </ul>
 */
export function pruneChoice(memberships: readonly TenantMembership[]): TenantChoice | null {
  // From the ACTIVE choice and not from storage: this page's requests are already going to the active
  // tenant, so that is the value the token's answer has to be weighed against. Re-reading storage here
  // would prune a value this page is not using and could hand the pill a tenant the header does not name.
  const choice = activeChoice();
  if (memberships.length === 0) {
    if (choice) chooseTenant(null);
    return null;
  }

  if (choice === null || choice.kind === "own") return choice;

  const current = memberships.find((m) => m.slug === choice.membership.slug) ?? null;
  if (!current) {
    chooseTenant(null);
    return null;
  }

  const refreshed: TenantChoice = { kind: "membership", membership: current };
  if (
    current.name !== choice.membership.name
    || current.tenantId !== choice.membership.tenantId
    || !sameRoles(current.roles, choice.membership.roles)
  ) {
    chooseTenant(refreshed);
  }

  return refreshed;
}
